import { motion } from "framer-motion";
import { Check, X, Lightbulb } from "lucide-react";
import { TABLE_ROWS, COMPARISON_SECTION } from "../data/home-data";

export default function ComparisonTable() {
    return (
        <section className="pt-9 pb-8" data-testid="comparison-table">
            <div className="max-w-4xl mx-auto px-4 sm:px-8">
                {/* Heading */}
                <div className="text-center mb-12">
                    <p className="text-xs uppercase tracking-[0.3em] text-aiorange font-bold mb-3">
                        {COMPARISON_SECTION.tag}
                    </p>
                    <h2
                        className="font-display font-black text-3xl sm:text-5xl tracking-normal max-w-2xl mx-auto"
                        dangerouslySetInnerHTML={{
                            __html: COMPARISON_SECTION.headline,
                        }}
                    />
                </div>

                {/* Table — scrolls horizontally on small screens so columns stay readable */}
                <div className="-mx-4 sm:mx-0 overflow-x-auto px-4 sm:px-0">
                  <div className="min-w-[560px] rounded-[20px] overflow-hidden border border-gray-100 bg-white shadow-sm">
                    {/* Header */}
                    <div className="grid grid-cols-[1fr_1.1fr_1.2fr]">
                        <div className="px-3 sm:px-5 py-[18px] bg-gray-50 border-r border-gray-100">
                            <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-gray-400">
                                {COMPARISON_SECTION.columnLabels.dimension}
                            </span>
                        </div>
                        <div className="px-3 sm:px-5 py-[18px] bg-gray-50 border-r border-gray-100 flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0">
                                <span className="text-gray-400 text-xs font-bold">
                                    ?
                                </span>
                            </div>
                            <span className="text-sm font-medium text-gray-800">
                                {COMPARISON_SECTION.columnLabels.traditional}
                            </span>
                        </div>
                        <div className="px-3 sm:px-5 py-[18px] bg-[#1a4fd8] flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-[#f97316] flex items-center justify-center shrink-0">
                                <Lightbulb className="h-3.5 w-3.5 text-white" />
                            </div>
                            <span className="text-sm font-medium text-white">
                                {COMPARISON_SECTION.columnLabels.aiprof}
                            </span>
                        </div>
                    </div>

                    {/* Rows */}
                    {TABLE_ROWS.map((row, i) => (
                        <motion.div
                            key={row.dimension}
                            initial={{ opacity: 0, y: 6 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.04 }}
                            className="grid grid-cols-[1fr_1.1fr_1.2fr] border-t border-gray-100 group"
                        >
                            <div className="px-3 sm:px-5 py-[14px] flex items-center bg-white group-hover:bg-gray-50 transition-colors border-r border-gray-100">
                                <span className="text-[13px] font-semibold text-gray-800">
                                    {row.dimension}
                                </span>
                            </div>
                            <div className="px-3 sm:px-5 py-[14px] flex items-center gap-2.5 bg-white group-hover:bg-red-50/40 transition-colors border-r border-gray-100">
                                <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                                    <X className="h-3 w-3 text-red-500 stroke-[3]" />
                                </div>
                                <span className="text-[13px] text-gray-500 leading-snug">
                                    {row.traditional}
                                </span>
                            </div>
                            <div className="px-3 sm:px-5 py-[14px] flex items-center gap-2.5 bg-[#f5f8ff] group-hover:bg-blue-100/50 transition-colors">
                                <div className="w-5 h-5 rounded-full bg-[#1a4fd8] flex items-center justify-center shrink-0">
                                    <Check className="h-3 w-3 text-white stroke-[3]" />
                                </div>
                                <span className="text-[13px] font-medium text-gray-900 leading-snug">
                                    {row.aiprof}
                                </span>
                            </div>
                        </motion.div>
                    ))}
                  </div>
                </div>

                {/* Bottom Quote Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-12 mx-auto max-w-3xl relative rounded-[32px] overflow-hidden shadow-xl"
                >
                    <div className="absolute inset-0 bg-[#0A2665]">
                        <div className="absolute top-0 left-0 w-[250px] h-[250px] bg-blue-500/30 blur-[80px] rounded-full -translate-x-1/2 -translate-y-1/2" />
                        <div className="absolute bottom-0 right-0 w-[250px] h-[250px] bg-orange-500/20 blur-[80px] rounded-full translate-x-1/4 translate-y-1/4" />
                    </div>
                    <div className="absolute top-4 left-4 w-4 h-4 border-t border-l border-white/20 rounded-tl-sm" />
                    <div className="absolute bottom-4 right-4 w-4 h-4 border-b border-r border-white/20 rounded-br-sm" />
                    <div className="relative p-8 md:p-10 text-center flex flex-col items-center justify-center">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-[1px] w-6 bg-orange-500/50"></div>
                            <span className="text-orange-500 text-[10px] font-bold tracking-[0.2em] uppercase">
                                {COMPARISON_SECTION.bottomQuote.label}
                            </span>
                            <div className="h-[1px] w-6 bg-orange-500/50"></div>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-black text-aiorange mb-4 font-display tracking-tight">
                            {COMPARISON_SECTION.bottomQuote.heading}
                        </h3>
                        <div className="flex flex-col md:flex-row items-center gap-2 text-base md:text-lg text-white font-medium mb-4">
                            <span>
                                {COMPARISON_SECTION.bottomQuote.subheading1}
                            </span>
                            <div className="w-1.5 h-1.5 rounded-full bg-orange-600/80 shadow-[0_0_8px_rgba(234,88,12,0.8)] hidden md:block"></div>
                            <span>
                                {COMPARISON_SECTION.bottomQuote.subheading2}
                            </span>
                        </div>
                        <p
                            className="text-gray-300 text-sm md:text-base font-medium"
                            dangerouslySetInnerHTML={{
                                __html: COMPARISON_SECTION.bottomQuote
                                    .description,
                            }}
                        />
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
